/*
 ************************************************************************
 *  © [2015 - 2023] Quintype Technologies India Private Limited
 *  All Rights Reserved.
 *************************************************************************
 */

import * as React from "react";
import {
  DragSource,
  DropTarget,
  DragSourceCollector,
  DropTargetCollector,
  DropTargetSpec,
  DragSourceSpec,
  DragElementWrapper,
  ConnectDropTarget
} from "react-dnd";
import { flow } from "lodash";

import CollectionCard, {
  Props as CollectionCardProps
} from "pages/collections/components/collection-card/collection-card";
import { CollectionItemWrapper } from "api/story-collection";

interface DragObject {
  item: CollectionItemWrapper;
}

interface DragSourceCollectedProps {
  connectDragSource: DragElementWrapper<any>;
  isDragging?: boolean;
}

interface DropTargetCollectedProps {
  connectDropTarget: ConnectDropTarget;
  isOver: boolean;
}

type SortedCollectionCardProps = CollectionCardProps & {
  onDragEnd: () => void;
  canDrag: boolean;
  connectDragSource: DragElementWrapper<any>;
  connectDropTarget: ConnectDropTarget;
  isOver: boolean;
};

const ITEM_TYPE = "item";

const dragSourceCollector: DragSourceCollector<DragSourceCollectedProps, any> = (connect, monitor, props) => {
  return {
    connectDragSource: connect.dragSource(),
    isDragging: monitor.isDragging()
  };
};

const dropTargetCollector: DropTargetCollector<DropTargetCollectedProps, any> = (connect, monitor, props) => {
  return {
    connectDropTarget: connect.dropTarget(),
    isOver: monitor.isOver()
  };
};

const dragSourceSpec: DragSourceSpec<SortedCollectionCardProps, DragObject> = {
  beginDrag: (props) => {
    return { item: props.item };
  },
  isDragging: (props, monitor) => {
    return props.item === monitor.getItem().item;
  },
  endDrag: (props) => {
    props.onDragEnd();
  },
  canDrag: (props) => {
    return props.canDrag;
  }
};

const dropTargetSpec: DropTargetSpec<any> = {
  hover: (props, monitor, component) => {
    var target = props.item,
      src = monitor.getItem().item;

    if (src !== target) {
      props.onDragOver(src, target);
    }
  }
};

const SortedCollectionCard: React.SFC<SortedCollectionCardProps &
  DragSourceCollectedProps &
  DropTargetCollectedProps> = (props) => {
  const { connectDragSource, connectDropTarget, ...rest } = props;

  return connectDragSource(
    connectDropTarget(
      <div data-test-id="collection-card">
        <CollectionCard {...rest} />
      </div>
    )
  );
};

export default flow(
  DragSource(ITEM_TYPE, dragSourceSpec, dragSourceCollector),
  DropTarget(ITEM_TYPE, dropTargetSpec, dropTargetCollector)
)(SortedCollectionCard);
